(ns bec-cljs-common.utils.formatting
  (:require [clojure.string :as s]
            [tick.core :as t]
            [re-frame.core :as rf]
            [taoensso.timbre :as log]
            [goog.string :as gstr]
            [bec-cljs-common.i18n :as i18n]
            [bec-cljs-common.re-frame.subs]))

(defn capitalize [string]
  (when (string? string)
    (s/capitalize string)))

(defn lower-case [string]
  (when (string? string)
    (s/lower-case string)))

(defn upper-case [string]
  (when (string? string)
    (s/upper-case string)))

(defn capitalize-words
  "Capitalize every word in a string"
  [string]
  (when (string? string)
    (->> (s/split (str string) #"\b")
      (map capitalize)
      s/join)))


(defn round-number [f & decimal-points]
  (let [move-decimal (if (first decimal-points)
                       (Math/pow 10 (first decimal-points))
                       10)]
    (/ (Math/round (* move-decimal f)) move-decimal)))

(defn to-seconds [{:keys [hours minutes seconds]}]
  (let [hours-to-seconds (* hours 3600)
        minutes-to-seconds (* minutes 60)]
    (+ hours-to-seconds minutes-to-seconds seconds)))

(defn seconds->minutes [seconds]
  (let [minutes (int (/ seconds 60))
        remaining-seconds (mod seconds 60)]
    [minutes remaining-seconds]))

(defn to-hours [{:keys [minutes seconds]}]
  (let [seconds-in-minutes (seconds->minutes seconds)
        minutes-from-seconds (first seconds-in-minutes)
        remaining-seconds (second seconds-in-minutes)
        total-minutes (+ minutes minutes-from-seconds)
        hours (int (/ total-minutes 60))
        remaining-minutes (mod total-minutes 60)]
    [hours remaining-minutes remaining-seconds]))

(defn readable-duration [{:keys [duration-seconds
                                 seconds
                                 separator
                                 short?
                                 include-zero]}]
  (let [seconds (or seconds duration-seconds)
        negative? (neg? seconds)
        int-seconds (int (if negative? (- seconds) seconds))
        negative-text (when negative? "-")
        days        (quot int-seconds 86400)
        days-r      (rem int-seconds 86400)
        hours       (quot days-r 3600)
        hours-r     (rem days-r 3600)
        minutes     (quot hours-r 60)
        seconds     (rem hours-r 60)
        days-text (if short?
                    (i18n/get {:fallback-phrase "d"} [:time :day-abbreviation])
                    (if (= days 1)
                      (str " " (lower-case (i18n/get {:fallback-phrase "day"} [:time :day])))
                      (str " " (lower-case (i18n/get {:fallback-phrase "days"} [:time :days])))))
        hours-text (if short?
                     (i18n/get {:fallback-phrase "h"} [:time :hour-abbreviation])
                     (if (= hours 1)
                       (str " " (lower-case (i18n/get {:fallback-phrase "hour"} [:time :hour])))
                       (str " " (lower-case (i18n/get {:fallback-phrase "hours"} [:time :hours])))))
        minutes-text (if short?
                       (i18n/get {:fallback-phrase "m"} [:time :minute-abbreviation])
                       (if (= minutes 1)
                         (str " " (lower-case (i18n/get {:fallback-phrase "minute"} [:time :minute])))
                         (str " " (lower-case (i18n/get {:fallback-phrase "minutes"} [:time :minutes])))))
        seconds-text (if short?
                       (i18n/get {:fallback-phrase "s"} [:time :second-abbreviation])
                       (if (= seconds 1)
                         (str " " (lower-case (i18n/get {:fallback-phrase "second"} [:time :second])))
                         (str " " (lower-case (i18n/get {:fallback-phrase "seconds"} [:time :seconds])))))]
    (s/join (or separator " ")
      (filter #(not (s/blank? %))
        (conj []
          (when (or (:days? include-zero) (> days 0)) (str negative-text days days-text))
          (when (or (:hours? include-zero) (> hours 0)) (str negative-text hours hours-text))
          (when (or (:minutes? include-zero) (> minutes 0)) (str negative-text minutes minutes-text))
          (when (or (:seconds? include-zero) (> seconds 0)) (str negative-text seconds seconds-text)))))))

(defn format-time-short [{:keys [seconds
                                 minutes
                                 hours]}]
  (cond
    (or (< seconds 0) (< minutes 0) (< hours 0))
    (i18n/get {:fallback-phrase "Out of time"} [:time :out-of-time])

    (and seconds minutes hours)
    (gstr/format "%02d:%02d:%02d"
      hours minutes seconds)

    (and minutes hours)
    (gstr/format "%02d:%02d"
      hours minutes)

    (and seconds minutes)
    (gstr/format "%02d:%02d"
      minutes seconds)

    seconds
    (gstr/format "%02d"
      seconds)
    :else (i18n/get {:fallback-phrase "Out of time"} [:time :out-of-time])))

(defn instant-breakdown
  "Takes an instant of time and breaks it down into units."
  [t]
  {:day  (t/day-of-week t)
   :month  (t/month t)
   :dd (t/day-of-month t)
   :MM (t/int (t/month t))
   :yyyy (t/int (t/year t))
   :mm (t/minute t)
   :HH (t/hour t)
   :ss (t/second t)})

(defn format-seconds [{:keys [seconds
                              format]}]
  (let [int-seconds (int seconds)
        days-r (rem int-seconds 86400)
        hours (quot days-r 3600)
        hours-r (rem days-r 3600)
        minutes-in-hours (quot hours-r 60)
        seconds-in-minutes (rem hours-r 60)
        timer {:ss       {:seconds int-seconds}
               :mm-ss    {:seconds seconds-in-minutes
                          :minutes minutes-in-hours}
               :HH-mm-ss {:seconds seconds-in-minutes
                          :minutes minutes-in-hours
                          :hours   hours}}]
    (format-time-short (get timer format))))

(defn format-date [t & [locale]]
  (let [t-break (instant-breakdown t)
        month (if (< (:MM t-break) 10)
                (str "0" (:MM t-break))
                (:MM t-break))
        day (if (< (:dd t-break) 10)
              (str "0" (:dd t-break))
              (:dd t-break))]
    (case locale
      ("en-US", "es-US")
      (str (:yyyy t-break) "." day "." month)
      (str (:yyyy t-break) "." month "." day))))

(defn format-time [t & [locale]]
  (let [t-break (instant-breakdown t)
        hour (if (< (:HH t-break) 10)
               (str "0" (:HH t-break))
               (:HH t-break))
        minute (if (< (:mm t-break) 10)
                 (str "0" (:mm t-break))
                 (:mm t-break))]
    (str hour ":" minute)))

(defn format-tick-ts [utc-ts & [{:keys [date? locale time-zone]}]]
  (when utc-ts
    (let [locale (or locale @(rf/subscribe [:locale]))
          converted-time (t/in utc-ts (or time-zone @(rf/subscribe [:time-zone])))]
      (if date?
        (format-date converted-time locale)
        (format-time converted-time locale)))))